import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import EmptyState from '@embracesbs/component-emptystate'
import LoadingState from '../Components/Common/LoadingState'
import BandBackButton from '../Components/Common/BandBackButton'
import { useApi, useUserPreferences } from '../Hooks'
import { ModuleOverviewPackage, ModuleOverviewResponse } from '../Interfaces/Types'

const PAGE_SIZES = [25, 50, 100]

const bandStyle: CSSProperties = {
  flexShrink: 0,
  height: '52px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 16px',
  backgroundColor: 'var(--tma-band-bg)'
}

const chipsWrapStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  alignItems: 'center'
}

// Tiny ‹/› column-reorder buttons in the module headers.
const moveButtonStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--tma-text-faint)',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: 1,
  padding: '0 2px',
  fontFamily: 'inherit'
}

const thBase: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--tma-card-bg)',
  borderBottom: '1px solid var(--tma-list-card-border)',
  padding: '8px 12px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--tma-text-strong)'
}

const tdBase: CSSProperties = {
  borderBottom: '1px solid var(--tma-card-border)',
  padding: '6px 12px',
  whiteSpace: 'nowrap',
  fontSize: '13px',
  // Without an explicit color the cells inherit browser-default black,
  // which is unreadable on the dark theme's row surfaces.
  color: 'var(--tma-text-strong)'
}

// Short column captions: every module package ends in a "-modules"-like suffix
// that only repeats noise in the header.
const shortPackageName = (name: string) => name.replace(/-(tenant-)?modules?$/i, '')

// The version tenants are compared against: what the code's pin installs, or the latest when the
// package isn't pinned or the feed can't be read (mirrors ModuleCollectionController).
const targetVersion = (pkg: ModuleOverviewPackage) => pkg.expectedVersion ?? pkg.latestVersion

// A newer version is published than the code installs, so the pin in tf-build.yml needs raising
// before tenants can get it.
const pinBehindLatest = (pkg: ModuleOverviewPackage) =>
  !!pkg.expectedVersion && !!pkg.latestVersion && pkg.expectedVersion !== pkg.latestVersion

// Timestamps are written as UTC but EF reads them back Kind=Unspecified, so they
// serialise without a zone suffix — and a bare ISO string parses as LOCAL time.
// Same normalisation as SidebarAdminTools (WI 165958).
const formatUtcTimestamp = (iso: string): string => {
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso)
  return new Date(hasZone ? iso : `${iso}Z`).toLocaleString()
}

const appFontFamily = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const ModulesOverview: React.FC = () => {
  const navigate = useNavigate()
  const { fetchApi } = useApi()
  const { preferences, update } = useUserPreferences()

  const [environmentOptions, setEnvironmentOptions] = useState<string[]>([])
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([])
  const [tenantQuery, setTenantQuery] = useState('')
  // Per-package version filter as typed in the column inputs ("1.4", "!1.4.2").
  const [versionFilters, setVersionFilters] = useState<Record<string, string>>({})
  const [sortBy, setSortBy] = useState('tenantName')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(PAGE_SIZES[0])

  const [data, setData] = useState<ModuleOverviewResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Only text input should debounce the fetch — a paging/sort/chip click is a
  // deliberate action and fires immediately.
  const debounceNextFetch = useRef(false)

  useEffect(() => {
    fetchApi({ endpoint: '/api/search/availableFilters', method: 'GET' })
      .then((response) => setEnvironmentOptions(response?.environments || []))
      .catch((err) => console.error('Error fetching environments:', err))
  }, [fetchApi])

  const filtersParam = useMemo(
    () =>
      Object.entries(versionFilters)
        .map(([pkg, value]) => [pkg, value.trim()] as const)
        .filter(([, value]) => value.length > 0)
        .map(([pkg, value]) => `${pkg}:${value}`)
        .join(','),
    [versionFilters]
  )

  const loadOverview = useCallback(
    (signal?: AbortSignal) => {
      const params = new URLSearchParams({
        skip: String(skip),
        take: String(take),
        sortBy,
        sortDir,
        ...(selectedEnvironments.length > 0 && { environment: selectedEnvironments.join(',') }),
        ...(tenantQuery.trim() && { tenantName: tenantQuery.trim() }),
        ...(filtersParam && { filters: filtersParam })
      })

      return fetchApi({ endpoint: `/api/modulecollection/overview?${params.toString()}`, method: 'GET', signal })
    },
    [fetchApi, skip, take, sortBy, sortDir, selectedEnvironments, tenantQuery, filtersParam]
  )

  // Typing in the search/version inputs debounces (the filter runs server-side
  // over the full tenant set — not one request per keystroke); other state
  // changes fetch immediately.
  useEffect(() => {
    const controller = new AbortController()
    const delay = debounceNextFetch.current ? 300 : 0
    debounceNextFetch.current = false
    setIsLoading(true)
    setError(null)

    const handler = setTimeout(() => {
      loadOverview(controller.signal)
        .then((response) => {
          if (response) setData(response)
        })
        .catch((err) => {
          if (err?.name !== 'AbortError') {
            console.error('Error fetching module overview:', err)
            // Surface the server's message (e.g. an invalid filter) when it sent one.
            setError(typeof err?.data === 'string' && err.data ? err.data : 'Could not load the module overview.')
          }
        })
        .finally(() => {
          // A superseded (aborted) request must not clear the loading state of
          // the request that replaced it.
          if (!controller.signal.aborted) setIsLoading(false)
        })
    }, delay)

    return () => {
      clearTimeout(handler)
      controller.abort()
    }
  }, [loadOverview])

  const resetToFirstPage = () => setSkip(0)

  const toggleEnvironment = (env: string) => {
    resetToFirstPage()
    setSelectedEnvironments((current) => (current.includes(env) ? current.filter((e) => e !== env) : [...current, env]))
  }

  const toggleSort = (column: string) => {
    resetToFirstPage()
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir('asc')
    }
  }

  const setVersionFilter = (pkg: string, value: string, debounce = false) => {
    resetToFirstPage()
    if (debounce) debounceNextFetch.current = true
    setVersionFilters((current) => ({ ...current, [pkg]: value }))
  }

  // "N outdated" badge → toggle the "has the package, but not on the version a
  // deploy installs" filter for that column.
  const toggleOutdatedFilter = (pkg: string, targetVersion: string) => {
    const needle = `!${targetVersion}`
    setVersionFilter(pkg, (versionFilters[pkg] || '') === needle ? '' : needle)
  }

  const clearFilters = () => {
    resetToFirstPage()
    setSelectedEnvironments([])
    setTenantQuery('')
    setVersionFilters({})
  }

  const hasFilters = selectedEnvironments.length > 0 || tenantQuery.trim().length > 0 || filtersParam.length > 0

  const sortIndicator = (column: string) => (sortBy === column ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')

  // Module columns follow the user's preferred order (Settings-backed, roams
  // with the account); packages not in the saved order keep the API's order
  // at the end. The sort is stable, so an empty preference changes nothing.
  const orderedPackages = useMemo(() => {
    const base = data?.packages ?? []
    const order = preferences.moduleOrder
    if (order.length === 0) return base
    const rank = (name: string) => {
      const i = order.indexOf(name)
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    return [...base].sort((a, b) => rank(a.name) - rank(b.name))
  }, [data, preferences.moduleOrder])

  // Persist the full effective order after a ‹/› move, so future API additions
  // still append at the end.
  const moveModule = (name: string, delta: number) => {
    const names = orderedPackages.map((p) => p.name)
    const from = names.indexOf(name)
    const to = from + delta
    if (from === -1 || to < 0 || to >= names.length) return
    names.splice(from, 1)
    names.splice(to, 0, name)
    update({ moduleOrder: names })
  }

  const packages = orderedPackages
  const rows = data?.rows ?? []
  const totalCount = data?.totalCount ?? 0
  const pageStart = totalCount === 0 ? 0 : skip + 1
  const pageEnd = Math.min(skip + take, totalCount)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--app-height, 100vh)',
        overflow: 'hidden',
        background: 'var(--tma-panel-bg)',
        fontFamily: appFontFamily
      }}
    >
      {/* Dark band matching the other pages, with the back action and page title. */}
      <div style={bandStyle}>
        <BandBackButton />
        <span
          style={{
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}
        >
          Terraform modules
        </span>
        <span style={{ color: 'var(--tma-band-text-muted)', fontSize: '12px' }}>version overview per tenant</span>
      </div>

      {/* Filter bar: stage chips + tenant search; the per-package version filters live in the table header. */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px',
          background: 'var(--tma-panel-bg)',
          borderBottom: '1px solid var(--tma-list-card-border)',
          padding: '10px 16px'
        }}
      >
        <div className='search-no-glow' style={{ width: '260px' }}>
          <TextField
            isSearchMode
            placeholder='Search for a tenant'
            value={tenantQuery}
            onChange={(e) => {
              resetToFirstPage()
              debounceNextFetch.current = true
              setTenantQuery(e.target.value)
            }}
            onClear={() => {
              resetToFirstPage()
              setTenantQuery('')
            }}
            type='text'
            variant='default'
          />
        </div>
        <div style={chipsWrapStyle}>
          <Text textStyle='description' textColor='grey' content='Stage' />
          {environmentOptions.map((env) => (
            <Button2
              key={env}
              text={env}
              variant='filter'
              isActive={selectedEnvironments.includes(env)}
              onClick={() => toggleEnvironment(env)}
              type='button'
            />
          ))}
        </div>
        {hasFilters && <Button2 text='Clear filters' variant='subtle' onClick={clearFilters} type='button' />}
        {preferences.moduleOrder.length > 0 && (
          <Button2
            text='Reset column order'
            variant='subtle'
            onClick={() => update({ moduleOrder: [] })}
            type='button'
            tooltip='Restore the default module column order'
          />
        )}
      </div>

      {/* The table itself; scrolls both ways, header sticks. */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }} className='custom-scrollbar'>
        {isLoading && !data && <LoadingState text='Loading module overview…' />}

        {error && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState imageName='error504' size='medium' title='Something went wrong' text={error} />
          </div>
        )}

        {!error && data && rows.length === 0 && !isLoading && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState
              imageName='searchempty'
              size='medium'
              title='No tenants'
              text={hasFilters ? 'Nothing matches your filters.' : 'No tenants found.'}
            />
          </div>
        )}

        {!error && data && rows.length > 0 && (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%', opacity: isLoading ? 0.6 : 1 }}>
            <thead>
              <tr>
                <th style={{ ...thBase, cursor: 'pointer', minWidth: '180px' }} onClick={() => toggleSort('tenantName')}>
                  Tenant{sortIndicator('tenantName')}
                </th>
                <th style={{ ...thBase, cursor: 'pointer' }} onClick={() => toggleSort('environment')}>
                  Stage{sortIndicator('environment')}
                </th>
                <th style={{ ...thBase, cursor: 'pointer' }} onClick={() => toggleSort('timestamp')}>
                  Modules updated{sortIndicator('timestamp')}
                </th>
                {packages.map((pkg, pkgIndex) => (
                  <th key={pkg.name} style={{ ...thBase, minWidth: '130px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => toggleSort(`package:${pkg.name}`)} title={pkg.name}>
                        {shortPackageName(pkg.name)}
                        {sortIndicator(`package:${pkg.name}`)}
                      </div>
                      {/* Column order is a user preference; it roams via /api/preferences. */}
                      <button
                        type='button'
                        onClick={() => moveModule(pkg.name, -1)}
                        disabled={pkgIndex === 0}
                        title='Move column left'
                        aria-label={`Move ${shortPackageName(pkg.name)} column left`}
                        style={moveButtonStyle}
                      >
                        ‹
                      </button>
                      <button
                        type='button'
                        onClick={() => moveModule(pkg.name, 1)}
                        disabled={pkgIndex === packages.length - 1}
                        title='Move column right'
                        aria-label={`Move ${shortPackageName(pkg.name)} column right`}
                        style={moveButtonStyle}
                      >
                        ›
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                      {pkg.expectedVersion ? (
                        <span
                          style={{ fontWeight: 400, color: 'var(--tma-text-muted)', fontSize: '11px' }}
                          title={`What a deploy installs: devops/pipelines/tf-build.yml pins ${pkg.pinnedVersion}, which resolves to ${pkg.expectedVersion} in the terraform feed`}
                        >
                          {`code ${pkg.pinnedVersion} → ${pkg.expectedVersion}`}
                        </span>
                      ) : (
                        <span
                          style={{ fontWeight: 400, color: 'var(--tma-text-muted)', fontSize: '11px' }}
                          title={
                            pkg.latestVersionSource === 'observed'
                              ? 'Highest version seen on a tenant — this package could not be resolved in the terraform feed'
                              : pkg.latestVersionSource === 'feed'
                                ? 'Latest version published in the terraform feed'
                                : 'No version information available'
                          }
                        >
                          {pkg.latestVersion ? `latest ${pkg.latestVersion}` : 'latest unknown'}
                          {pkg.latestVersionSource === 'observed' ? '*' : ''}
                        </span>
                      )}
                      {targetVersion(pkg) && pkg.outdatedCount > 0 && (
                        <span
                          onClick={() => toggleOutdatedFilter(pkg.name, targetVersion(pkg)!)}
                          title={`${pkg.outdatedCount} of ${pkg.tenantCount} tenants are not on ${targetVersion(pkg)} — click to filter`}
                          style={{
                            cursor: 'pointer',
                            background:
                              (versionFilters[pkg.name] || '') === `!${targetVersion(pkg)}` ? '#fd3838' : 'var(--tma-danger-tint)',
                            color: (versionFilters[pkg.name] || '') === `!${targetVersion(pkg)}` ? '#ffffff' : 'var(--tma-danger-text)',
                            borderRadius: '10px',
                            padding: '1px 8px',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {pkg.outdatedCount}
                        </span>
                      )}
                      {targetVersion(pkg) && pkg.outdatedCount === 0 && (
                        <span
                          title={`All ${pkg.tenantCount} tenants are on ${targetVersion(pkg)}`}
                          style={{ color: 'var(--tma-success-text)', fontSize: '11px', fontWeight: 600 }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    {pkg.expectedVersion && pkg.latestVersion && (
                      <div
                        style={{
                          marginTop: '2px',
                          fontSize: '11px',
                          fontWeight: pinBehindLatest(pkg) ? 600 : 400,
                          color: pinBehindLatest(pkg) ? 'var(--tma-warning-text)' : 'var(--tma-text-muted)'
                        }}
                        title={
                          pinBehindLatest(pkg)
                            ? `${pkg.latestVersion} is published, but the code still pins ${pkg.pinnedVersion}. Raise the pin in devops/pipelines/tf-build.yml to bring tenants to it.`
                            : 'Latest version published in the terraform feed'
                        }
                      >
                        {pinBehindLatest(pkg) ? `latest ${pkg.latestVersion} ↑` : `latest ${pkg.latestVersion}`}
                      </div>
                    )}
                    <input
                      value={versionFilters[pkg.name] || ''}
                      onChange={(e) => setVersionFilter(pkg.name, e.target.value, true)}
                      placeholder='filter version'
                      title='Version prefix ("1.4"), or !version for "not on this version" ("!1.4.2")'
                      style={{
                        marginTop: '4px',
                        width: '100px',
                        fontSize: '11px',
                        padding: '2px 6px',
                        border: '1px solid var(--tma-card-border)',
                        borderRadius: '4px',
                        fontWeight: 400,
                        // Form controls don't inherit the page font or theme
                        // colors by default (they'd render white/black in dark).
                        fontFamily: 'inherit',
                        background: 'var(--tma-panel-bg)',
                        color: 'var(--tma-text-strong)'
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.tenantId}
                  onClick={() => navigate(`/subscriptions/${row.subscriptionId}/tenant/${row.tenantId}`)}
                  style={{ cursor: 'pointer' }}
                  className='modules-overview-row'
                >
                  <td style={tdBase}>
                    <div style={{ fontWeight: 600 }}>{row.tenantName}</div>
                    {row.tenantDisplayName && row.tenantDisplayName !== row.tenantName && (
                      <div style={{ color: 'var(--tma-text-muted)', fontSize: '11px' }}>{row.tenantDisplayName}</div>
                    )}
                  </td>
                  <td style={tdBase}>{row.environment}</td>
                  <td
                    style={{ ...tdBase, color: row.timestamp ? 'var(--tma-text-muted)' : 'var(--tma-text-faint)' }}
                    title={row.timestamp ? undefined : 'No module information recorded for this tenant'}
                  >
                    {row.timestamp ? formatUtcTimestamp(row.timestamp) : '—'}
                  </td>
                  {packages.map((pkg) => {
                    const cell = row.modules[pkg.name]
                    if (!cell) {
                      return (
                        <td key={pkg.name} style={{ ...tdBase, color: 'var(--tma-text-faint)' }}>
                          —
                        </td>
                      )
                    }
                    return (
                      <td
                        key={pkg.name}
                        style={{
                          ...tdBase,
                          color: cell.isOutdated ? 'var(--tma-danger-text)' : 'var(--tma-text-strong)',
                          fontWeight: cell.isOutdated ? 600 : 400
                        }}
                        title={
                          cell.isOutdated
                            ? pkg.expectedVersion
                              ? `Not on ${pkg.expectedVersion}, the version a deploy installs`
                              : `Not on latest (${pkg.latestVersion})`
                            : 'Up to date'
                        }
                      >
                        {cell.version}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paging bar. */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderTop: '1px solid var(--tma-list-card-border)',
          padding: '8px 16px',
          background: 'var(--tma-panel-bg)'
        }}
      >
        <Text textStyle='description' textColor='grey' content={`${pageStart}–${pageEnd} of ${totalCount} tenants`} />
        <Button2 text='Previous' variant='subtle' type='button' isDisabled={skip === 0} onClick={() => setSkip(Math.max(skip - take, 0))} />
        <Button2 text='Next' variant='subtle' type='button' isDisabled={pageEnd >= totalCount} onClick={() => setSkip(skip + take)} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Text textStyle='description' textColor='grey' content='Per page' />
          {PAGE_SIZES.map((size) => (
            <Button2
              key={size}
              text={String(size)}
              variant='filter'
              isActive={take === size}
              type='button'
              onClick={() => {
                resetToFirstPage()
                setTake(size)
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ModulesOverview
